import * as React from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import MuiAlert from "@mui/material/Alert";
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Paper,
  Snackbar,
} from "@mui/material";
import { IoEye } from "react-icons/io5";
import { IoEyeOff } from "react-icons/io5";
import * as EmailValidator from "email-validator";
import { LoadingButton } from "@mui/lab";
import { resetPasswordWithOtp } from "../api/vendorAuthApi";

const defaultTheme = createTheme();

function Copyright(props) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {"Copyright © "}
      <Link color="inherit" href="/">
        Shiftnpay, LLC, a Computerlog Company
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function ResetPasswordWithOtp() {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromState = location.state?.email || "";
  const [open, setOpen] = React.useState(false);
  const [customVariant, setCustomVariant] = React.useState("success");
  const [message, setMessage] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [form, setForm] = React.useState({
    email: emailFromState,
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = React.useState({
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    const newErrors = {};
    if (!form.email) newErrors.email = "Email is required";
    else if (!EmailValidator.validate(form.email)) newErrors.email = "Invalid email";
    if (!form.otp) newErrors.otp = "OTP is required";
    if (!form.newPassword) newErrors.newPassword = "New password is required";
    else if (form.newPassword.length < 5) newErrors.newPassword = "Password must be at least 5 characters";
    if (form.newPassword !== form.confirmPassword) newErrors.confirmPassword = "Passwords do not match";
    if (Object.keys(newErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return;
    }
    setLoading(true);
    try {
      await resetPasswordWithOtp(form.email, form.otp, form.newPassword);
      setMessage("Password reset successfully. You can now sign in.");
      setCustomVariant("success");
      setOpen(true);
      setTimeout(() => navigate("/signin"), 2000);
    } catch (err) {
      setMessage(
        err?.response?.data?.msg || err?.response?.data?.message || "Failed to reset password. Please try again."
      );
      setCustomVariant("error");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleMouseDownPassword = (e) => e.preventDefault();

  return (
    <ThemeProvider theme={defaultTheme}>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleClose} severity={customVariant} sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
      <Grid container component="main" sx={{ height: "100vh" }}>
        <CssBaseline />
        <Grid
          item
          xs={false}
          sm={4}
          md={7}
          sx={{
            backgroundImage: "url(https://shiftnpay.com/api/images/login/regisrationImage.png)",
            width: "100%",
            height: "auto",
            backgroundColor: (t) => (t.palette.mode === "light" ? t.palette.grey[50] : t.palette.grey[900]),
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "top !important",
          }}
        />
        <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
          <Box sx={{ my: 8, mx: 4, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box sx={{ width: 200, height: 150 }}>
              <img src="./logo/sky.png" alt="logo" style={{ width: "100%", height: "100%" }} />
            </Box>
            <Typography component="h1" variant="h5">
              Reset Password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center" }}>
              Enter the OTP sent to your email and your new password.
            </Typography>
            <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 2, width: "100%" }}>
              <TextField
                error={!!errors.email}
                helperText={errors.email}
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                sx={{ mb: 2 }}
                autoComplete="email"
                disabled={!!emailFromState}
              />
              <TextField
                error={!!errors.otp}
                helperText={errors.otp}
                required
                fullWidth
                id="otp"
                label="OTP"
                name="otp"
                value={form.otp}
                onChange={handleChange}
                inputProps={{ maxLength: 6 }}
                sx={{ mb: 2 }}
                placeholder="Enter 4–6 digit OTP"
              />
              <FormControl variant="outlined" fullWidth sx={{ mb: 2 }}>
                <InputLabel htmlFor="new-password">New Password</InputLabel>
                <OutlinedInput
                  id="new-password"
                  name="newPassword"
                  type={showPassword ? "text" : "password"}
                  value={form.newPassword}
                  onChange={handleChange}
                  error={!!errors.newPassword}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((s) => !s)}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <IoEyeOff /> : <IoEye />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="New Password"
                />
                <Typography variant="caption" color="error">
                  {errors.newPassword}
                </Typography>
              </FormControl>
              <FormControl variant="outlined" fullWidth sx={{ mb: 2 }}>
                <InputLabel htmlFor="confirm-password">Confirm Password</InputLabel>
                <OutlinedInput
                  id="confirm-password"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={form.confirmPassword}
                  onChange={handleChange}
                  error={!!errors.confirmPassword}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword((s) => !s)}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? <IoEyeOff /> : <IoEye />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Confirm Password"
                />
                <Typography variant="caption" color="error">
                  {errors.confirmPassword}
                </Typography>
              </FormControl>
              {loading ? (
                <LoadingButton loading fullWidth variant="contained" sx={{ mt: 2, mb: 2 }}>
                  Resetting
                </LoadingButton>
              ) : (
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2, mb: 2, bgcolor: "#108A00", "&:hover": { bgcolor: "#0D6E00" } }}
                >
                  Reset Password
                </Button>
              )}
              <Grid container justifyContent="space-between">
                <Grid item>
                  <Link to="/forgot-password" state={{ email: form.email }} variant="body2">
                    Resend OTP
                  </Link>
                </Grid>
                <Grid item>
                  <Link to="/signin" variant="body2">
                    Back to Sign In
                  </Link>
                </Grid>
              </Grid>
              <Copyright sx={{ mt: 5 }} />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
}
